/**
 * Single source of truth for primary app sidebar / mobile nav structure.
 * Only include paths that exist in App.js (or documented redirects).
 */

import {
  Home,
  Image as ImageIcon,
  MessageCircle,
  Heart,
  LayoutGrid,
  Info,
  MessageSquareText,
  FileText,
  Shield,
  Palette,
  UserCircle,
  PlusCircle,
  Tv,
  Users,
} from "lucide-react";

/** Canonical paths used across the app shell */
export const APP_ROUTES = {
  home: "/",
  community: "/community",
  arthouse: "/arthouse",
  clubs: "/clubs",
  clubsDiscover: "/clubs/discover",
  clubsMy: "/clubs/my-clubs",
  clubsNew: "/clubs/new",
  watchParties: "/watch-parties",
  profile: "/profile",
  login: "/login",
  messages: "/messages",
  favorites: "/favorites",
  hallOfFame: "/hall-of-fame",
  about: "/about",
  feedback: "/feedback",
  terms: "/terms",
  privacy: "/privacy",
  dashboardArt: "/dashboard/art",
  dashboardProfile: "/dashboard/profile",
  dashboardNewArt: "/dashboard/art/new",
};

/**
 * @typedef {Object} NavItem
 * @property {string} key
 * @property {string} label
 * @property {string} to
 * @property {import('lucide-react').LucideIcon} Icon
 * @property {string} [subtitle]
 */

/**
 * @typedef {Object} NavSection
 * @property {string} id
 * @property {string} title
 * @property {NavItem[]} items
 * @property {boolean} [defaultOpen]
 * @property {boolean} [requireAuth]
 */

/** Primary rail: compact Reddit-style top links (no /search in nav). */
export function getMainNavItems() {
  /** @type {NavItem[]} */
  const items = [
    {
      key: "home",
      label: "Home",
      to: APP_ROUTES.home,
      Icon: Home,
    },
    {
      key: "community",
      label: "Community",
      to: APP_ROUTES.community,
      Icon: LayoutGrid,
    },
    {
      key: "watch",
      label: "Watch",
      to: APP_ROUTES.watchParties,
      Icon: Tv,
    },
    {
      key: "arthouse",
      label: "ArtHouse",
      to: APP_ROUTES.arthouse,
      Icon: ImageIcon,
    },
    {
      key: "messages",
      label: "Messages",
      to: APP_ROUTES.messages,
      Icon: MessageCircle,
    },
  ];

  return items;
}

/** @param {{ isAuthenticated: boolean }} ctx */
export function getCollapsibleSections(ctx) {
  const { isAuthenticated } = ctx;

  /** @type {NavSection[]} */
  const sections = [
    {
      id: "resources",
      title: "Resources",
      defaultOpen: false,
      items: [
        ...(isAuthenticated
          ? [
              {
                key: "favorites",
                label: "Favorites",
                to: APP_ROUTES.favorites,
                Icon: Heart,
              },
            ]
          : []),
        {
          key: "about",
          label: "About",
          to: APP_ROUTES.about,
          Icon: Info,
        },
        {
          key: "feedback",
          label: "Feedback",
          to: APP_ROUTES.feedback,
          Icon: MessageSquareText,
        },
        {
          key: "terms",
          label: "Terms",
          to: APP_ROUTES.terms,
          Icon: FileText,
        },
        {
          key: "privacy",
          label: "Privacy",
          to: APP_ROUTES.privacy,
          Icon: Shield,
        },
      ],
    },
  ];

  if (isAuthenticated) {
    sections.push({
      id: "creator",
      title: "Creator",
      defaultOpen: false,
      requireAuth: true,
      items: [
        {
          key: "dash-art",
          label: "Artist dashboard",
          to: APP_ROUTES.dashboardArt,
          Icon: Palette,
        },
        {
          key: "dash-profile",
          label: "Studio profile",
          to: APP_ROUTES.dashboardProfile,
          Icon: UserCircle,
        },
        {
          key: "dash-new",
          label: "Upload artwork",
          to: APP_ROUTES.dashboardNewArt,
          Icon: PlusCircle,
        },
      ],
    });
  }

  return sections;
}

/** Mobile bottom bar — no top-level “Clubs”; communities entry is discover. */
export function getMobilePrimaryTabs() {
  return [
    {
      key: "home",
      label: "Home",
      to: APP_ROUTES.home,
      Icon: Home,
    },
    {
      key: "community",
      label: "Community",
      to: APP_ROUTES.community,
      Icon: LayoutGrid,
    },
    {
      key: "arthouse",
      label: "ArtHouse",
      to: APP_ROUTES.arthouse,
      Icon: ImageIcon,
    },
    {
      key: "communities",
      label: "Communities",
      to: APP_ROUTES.clubsDiscover,
      Icon: Users,
      /** Highlight for any club/community route, not only discover. */
      activeBase: "/clubs",
    },
    {
      key: "watch",
      label: "Watch",
      to: APP_ROUTES.watchParties,
      Icon: Tv,
    },
    {
      key: "messages",
      label: "Messages",
      to: APP_ROUTES.messages,
      Icon: MessageCircle,
    },
  ];
}
